import { useState } from 'react';
import { Button } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { AxiosService } from '../../../../../../../axios/axios.service';
import { useWorkspaceContext } from '../../../../../../../providers/WorkspaceProvider/Workspace.context.ts';
import { useMetaConnectionModalContext } from '../../MetaConnectionModal.hook.ts';

type tMetaConnectStart = {
  url: string;
  error?: { code?: number };
};

/**
 * BUG-1083: which refusal the user is looking at. `forbidden` is the one a message can
 * resolve — the member is not authorized to start the Meta flow in this workspace and
 * only the owner can — so it earns its own copy; everything else is `generic`.
 */
type tStartFailure = 'forbidden' | 'generic';

/**
 * The 403 reaches the front in two shapes, and the user must read the same explanation
 * for both: an HTTP 403 since BUG-545 made the service's status agree with the code, and
 * a resolved 200 envelope carrying `error.code` before that — the shape the regression
 * tests in this folder still pin.
 */
const FORBIDDEN = 403;

function failureOf(error: unknown): tStartFailure {
  const status = (error as { response?: { status?: number } })?.response?.status;
  const bodyCode = (error as { response?: { data?: { error?: { code?: number } } } })
    ?.response?.data?.error?.code;

  return status === FORBIDDEN || bodyCode === FORBIDDEN ? 'forbidden' : 'generic';
}

export default function MetaStepInfo() {
  const { setStep } = useMetaConnectionModalContext();
  const [failure, setFailure] = useState<tStartFailure | null>(null);
  const { currentWorkspaceId } = useWorkspaceContext();
  const { t } = useTranslation('dashboard/dashboard');

  /**
   * Starting the Meta connect flow is an AUTHENTICATED CALL now, not a bare navigation.
   *
   * It used to build the gateway URL here and `window.open` it. That URL —
   * `/meta-messenger/auth/redirect/{provider}/{wid}` — accepted any workspace id from
   * anyone on the internet and handed back a redirect to Facebook carrying it as an
   * unsigned OAuth `state`, so a stranger could start a connect flow aimed at someone
   * else's workspace (BUG-856).
   *
   * `/auth/start` fixes it where it can be fixed: the service checks that the caller owns
   * the workspace and returns a consent URL whose `state` is signed, single-use and short
   * lived. It is an ordinary `AxiosService` call, so the Firebase token travels in the
   * `Authorization` header exactly as it does everywhere else.
   *
   * A cookie was considered and rejected: one the front can write must be scoped to
   * `.leadsales.services`, which sends the token to every host under that domain — grafana
   * and backoffice among them — and exposes it to JavaScript on all of them. `sessionStorage`
   * is origin-scoped and does neither.
   *
   * THE WINDOW IS OPENED BEFORE THE CALL IS AWAITED, ON PURPOSE. `window.open` is only
   * permitted while the browser still considers itself inside the user gesture that
   * triggered it; awaiting first ends that window and popup blockers reject the call. So the
   * tab is claimed synchronously and pointed at the URL once it arrives.
   */
  const openMetaWindow = () => {
    const popup = window.open('', '_blank');

    // The button stays pressable, so a stale refusal must not outlive the retry that
    // fixes it.
    setFailure(null);

    void AxiosService.get<tMetaConnectStart>(
      `/meta-messenger/auth/start/facebook/${currentWorkspaceId}`,
      {
        headers: {},
        isLegacy: true,
        // This flow handles its own failures now, so it must opt out of the shared
        // 401 auto-logout: the service throws 401 whenever the edge did not set
        // `X-Auth-User-Id` — a routing condition, not a dead session — and the
        // interceptor would purge sessionStorage, sign the Firebase user out and
        // navigate to `/` before the user could read anything. BUG-868's comment
        // names this exact opt-out as what a caller that handles its own 401 does.
        skipAutoRedirect: true,
        // The 403 is an answer of this flow, explained in the UI on purpose
        // (BUG-1083). Without this, a workspace in the broken state raises a Sentry
        // issue on every click of every affected member.
        skipSentryCaptureForStatuses: [403],
      },
    )
      .then((data) => {
        // A FAILURE CAN ARRIVE ON EITHER BRANCH, so both must handle it.
        //
        // meta-messenger-service USED TO wrap every thrown exception in an HTTP 200 whose
        // body carried the real code, which is why this branch exists at all: `AxiosService`
        // resolves with that body, `.catch` never runs, and destructuring `url` straight
        // away would navigate the popup to the string "undefined".
        //
        //   caller is not a member -> 200 {"error":{"code":403, …}}
        //
        // BUG-545 changed that: the service's Handler now makes the HTTP status agree with
        // the exception code, so in production today the same refusal arrives as a real
        // 403 and lands in `.catch` below — verified in Loki against the deployed service
        // (BUG-1083). The 200 envelope is kept covered because nothing guarantees every
        // path through that Handler was converted, and the regression tests in this folder
        // still pin it.
        const url = typeof data?.url === 'string' ? data.url : '';
        if (!url) {
          popup?.close();
          // Same refusal as the HTTP 403, just wrapped in a 200 — it must read the same
          // to the user, so the envelope's own code decides the copy.
          setFailure(data?.error?.code === FORBIDDEN ? 'forbidden' : 'generic');

          return;
        }

        if (popup) {
          popup.location.href = url;
        } else {
          // Blocked despite the synchronous open; a same-tab navigation still completes the
          // flow rather than failing silently.
          window.location.href = url;
        }

        setStep(3);
      },
      // SECOND ARGUMENT of this same `.then`, not a chained `.catch`: a chained one also
      // catches whatever the success handler throws — assigning `popup.location.href`
      // can throw after a URL was issued — and would blame the request for a failure
      // downstream of it. The trailing catch below closes the tab without that story.
      (error: unknown) => {
        // The gateway's 401 when there is no valid session, a 403 from the service's own
        // ownership check, or a transport failure. Navigating anyway would strand the user
        // on an error page in a tab they cannot act on; close it and leave the modal on
        // this step so the button can be pressed again — with an explanation this time,
        // because a tab that blinks shut is indistinguishable from a dead button.
        popup?.close();
        setFailure(failureOf(error));
      })
      .catch(() => {
        // Only reachable when the success handler itself threw. The request worked, so
        // there is no refusal to explain; just do not leave a tab stranded.
        popup?.close();
      });
  };

  return (
    <div className="MetaConnectionModal__Container">
      <div className="MetaConnectionModal__Container__Logo">
        <div className="MetaConnectionModal__Container__Logo__Bg">
          <img
            src="/images/icons/messenger-icon.png"
            alt="messenger-logo"
            className="MetaConnectionModal__Container__Logo__Icon "
          />
        </div>
      </div>

      <p className="MetaConnectionModal__Container__Title">
        {t('components.modals.MetaConnection.metaStepInfo.title')}
      </p>
      <p className="MetaConnectionModal__Container__Description">
        {t('components.modals.MetaConnection.metaStepInfo.description')}
      </p>
      <p className="MetaConnectionModal__Container__SubTitle">
        {t('components.modals.MetaConnection.metaStepInfo.subtitle')}
      </p>
      <div className="MetaConnectionModal__Container__Text">
        <p>{t('components.modals.MetaConnection.metaStepInfo.text1')}</p>
        <p>{t('components.modals.MetaConnection.metaStepInfo.text2')}</p>
        <p>{t('components.modals.MetaConnection.metaStepInfo.text3')}</p>
        <p>{t('components.modals.MetaConnection.metaStepInfo.text4')}</p>
      </div>
      {/*
        INT-1407: the two mistakes made inside Meta's window, after this button. "Continuar"
        on Meta's re-consent screen replays the previous grant, which may not include the
        Instagram account; and a teammate's Facebook session left open in the browser runs
        the whole connect as that other account.
      */}
      <div className="MetaConnectionModal__Container__Text mt-4">
        <p>
          {t('components.modals.MetaConnection.metaStepInfo.editSettingsHint')}
        </p>
        <p>
          {t(
            'components.modals.MetaConnection.metaStepInfo.facebookAccountHint',
          )}
        </p>
      </div>
      {failure && (
        <p className="MetaConnectionModal__Container__Error" role="alert">
          {t(`components.modals.MetaConnection.metaStepInfo.error.${failure}`)}
        </p>
      )}
      <div className="MetaConnectionModal__ButtonContainer">
        <Button
          onClick={openMetaWindow}
          color="primary"
          size="sm"
          className="MetaConnectionModal__ButtonContainer___Button text-base-100"
        >
          {t('components.modals.MetaConnection.metaStepInfo.button')}
        </Button>
      </div>
    </div>
  );
}
